import { produce } from 'immer';
import { type ActionDispatch, type ReactNode, createContext, useContext, useReducer } from 'react';
import { tabDetailsObjects } from '../../config/admin';
import type { Tab, TabType } from '../../types/tab';

type TabProviderProps = {
    activeTabKey?: string;
    tabs?: Tab[];
    dispatchTabs?: () => void;
    children: ReactNode;
};

type TabsState = {
    tabs: Tab[];
    activeTabKey: string | null;
};

const initialState: TabsState = {
    tabs: [],
    activeTabKey: null,
};

const TabsContext = createContext<TabsState>(initialState);
const TabsDispatchContext = createContext<ActionDispatch<[action: TabAction]> | null>(null);

export type TabAction =
    | {
          type: 'OPEN_FLOW';
          payload: { flowId: string; tabKey: string };
      }
    | {
          type: 'OPEN_PAGE';
          payload: { pageId: string; tabKey: string };
      }
    | {
          type: 'NEW_PAGE';
          payload: { title: string; tabKey: string };
      }
    | {
          type: 'OPEN_TAB';
          payload: {
              type?: TabType;
              tabKey?: string;
              elementId?: string | null;
              title?: string;
          };
      }
    | {
          type: 'CLOSE_TAB';
          payload: { tabKey: string };
      }
    | {
          type: 'CLOSE_ALL_TABS';
      }
    | {
          type: 'SET_CLOSE_CONFIRMATION_REQUIRED';
          payload: { tabKey: string; isRequired: boolean };
      }
    | {
          type: 'CLOSE_TAB_CONFIRMED';
          payload: { tabKey: string };
      }
    | {
          type: 'DISMISS_CLOSE_CONFIRMATION';
          payload: { tabKey: string };
      }
    | {
          type: 'SET_TITLE';
          payload: {
              tabKey: string;
              title: string;
          };
      }
    | {
          type: 'SET_ELEMENT_ID';
          payload: {
              tabKey: string;
              elementId: string;
              tabType?: TabType;
          };
      }
    | {
          type: 'SWITCH_FLOW';
          payload: {
              tabKey: string;
              flowId: string;
              flowName?: string | undefined;
          };
      };

const tabReducer = (tabsState: TabsState, action: TabAction): TabsState => {
    const nextState = produce(tabsState, (draftState) => {
        switch (action.type) {
            case 'OPEN_FLOW': {
                const tabConfig = tabDetailsObjects['flow'];
                const existingFlowTab = draftState.tabs.find(
                    (tab) => tab.type === 'flow' && tab.elementId === action.payload.flowId,
                );

                if (existingFlowTab) {
                    draftState.activeTabKey = existingFlowTab.key;
                    break;
                }

                draftState.activeTabKey = action.payload.tabKey;
                draftState.tabs.push({
                    key: action.payload.tabKey,
                    showCloseConfirmation: false,
                    requiresCloseConfirmation: false,
                    title: tabConfig.title ?? '',
                    type: 'flow',
                    elementId: action.payload.flowId,
                });

                break;
            }
            case 'SWITCH_FLOW': {
                draftState.tabs.forEach((tab) => {
                    if (tab.key === action.payload.tabKey) {
                        tab.elementId = action.payload.flowId;
                        if (action.payload.flowName) {
                            tab.title = action.payload.flowName;
                        }
                    }
                });
                break;
            }
            case 'NEW_PAGE': {
                const existingNewPageTab = draftState.tabs.find(
                    (tab) => tab.type === 'newPage' && tab.key === action.payload.tabKey,
                );

                if (existingNewPageTab) {
                    draftState.activeTabKey = existingNewPageTab.key;
                    break;
                }

                draftState.activeTabKey = action.payload.tabKey;
                draftState.tabs.push({
                    key: action.payload.tabKey,
                    showCloseConfirmation: false,
                    requiresCloseConfirmation: false,
                    title: action.payload.title,
                    type: 'newPage',
                    elementId: null,
                });

                break;
            }
            case 'OPEN_PAGE': {
                const existingPageTab = draftState.tabs.find(
                    (tab) => tab.type === 'page' && tab.elementId === action.payload.pageId,
                );

                if (existingPageTab) {
                    draftState.activeTabKey = existingPageTab.key;
                    break;
                }

                draftState.activeTabKey = action.payload.tabKey;
                draftState.tabs.push({
                    key: action.payload.tabKey,
                    showCloseConfirmation: false,
                    requiresCloseConfirmation: false,
                    title: tabDetailsObjects['page'].title ?? '',
                    type: 'page',
                    elementId: action.payload.pageId,
                });

                break;
            }
            case 'OPEN_TAB': {
                const tabConfig = action.payload.type
                    ? tabDetailsObjects[action.payload.type]
                    : null;

                const existingTab = draftState.tabs.find((tab) => {
                    if (tab.key === action.payload.tabKey) {
                        return true;
                    }
                    if (
                        action.payload.elementId !== null &&
                        tab.elementId === action.payload.elementId
                    ) {
                        return true;
                    }
                    if (
                        tab.type === action.payload.type &&
                        action.payload.elementId !== null &&
                        tab.elementId === action.payload.elementId
                    ) {
                        return true;
                    }
                    if (
                        tab.type === action.payload.type &&
                        tabConfig &&
                        !tabConfig.isMultiInstance &&
                        !action.payload.elementId
                    ) {
                        return true;
                    }
                    return false;
                });

                if (existingTab) {
                    draftState.activeTabKey = existingTab.key;
                    break;
                }

                if (!action.payload.tabKey) {
                    throw new Error('New tabs require a unique tab key');
                }

                if (!action.payload.type) {
                    throw new Error('New tabs require a tab type');
                }

                draftState.activeTabKey = action.payload.tabKey;
                draftState.tabs.push({
                    key: action.payload.tabKey,
                    showCloseConfirmation: false,
                    requiresCloseConfirmation: false,
                    title: action.payload.title ?? tabConfig?.title ?? '',
                    type: action.payload.type,
                    elementId: action.payload.elementId ?? null,
                });

                break;
            }
            case 'CLOSE_TAB': {
                const tab = draftState.tabs.find((tab) => tab.key === action.payload.tabKey);
                if (tab?.requiresCloseConfirmation) {
                    tab.showCloseConfirmation = true;
                    break;
                }

                closeTab(draftState, action.payload.tabKey);

                break;
            }
            case 'CLOSE_ALL_TABS':
                draftState.tabs = [draftState.tabs[0]];
                draftState.activeTabKey = draftState.tabs[0].key;
                break;
            case 'CLOSE_TAB_CONFIRMED':
                closeTab(draftState, action.payload.tabKey);
                break;
            case 'SET_CLOSE_CONFIRMATION_REQUIRED':
                draftState.tabs.forEach((tab) => {
                    if (
                        tab.key === action.payload.tabKey &&
                        tab.requiresCloseConfirmation !== action.payload.isRequired
                    ) {
                        tab.requiresCloseConfirmation = action.payload.isRequired;
                    }
                });
                break;
            case 'DISMISS_CLOSE_CONFIRMATION': {
                draftState.tabs.forEach((tab) => {
                    if (tab.key === action.payload.tabKey) {
                        tab.showCloseConfirmation = false;
                    }
                });
                break;
            }
            case 'SET_TITLE':
                draftState.tabs.forEach((tab) => {
                    if (tab.key === action.payload.tabKey) {
                        tab.title = action.payload.title;
                    }
                });
                break;
            case 'SET_ELEMENT_ID':
                draftState.tabs.forEach((tab) => {
                    if (tab.key === action.payload.tabKey) {
                        tab.elementId = action.payload.elementId;

                        if (action.payload.tabType) {
                            tab.type = action.payload.tabType;
                        }
                    }
                });
                break;
        }
    });

    return nextState;
};

const closeTab = (draftState: TabsState, tabKey: string) => {
    const index = draftState.tabs.findIndex((tab) => tab.key === tabKey);

    if (index === -1) {
        ('Cannot find a tab with a matching tab key');
    }

    const tabToRight = draftState.tabs[index + 1];
    const tabToLeft = draftState.tabs[index - 1];

    draftState.activeTabKey = tabToRight ? tabToRight.key : tabToLeft ? tabToLeft.key : null;

    draftState.tabs = draftState.tabs.filter((tab) => tab.key !== tabKey);
};

const TabsProvider = ({
    tabs = [
        {
            key: 'flows',
            showCloseConfirmation: false,
            requiresCloseConfirmation: false,
            title: 'Flows',
            type: 'flows',
            elementId: null,
        },
    ],
    dispatchTabs: mockedDispatch,
    activeTabKey = 'flows',
    children,
}: TabProviderProps) => {
    const [tabsState, dispatchTabs] = useReducer(tabReducer, { tabs, activeTabKey });

    return (
        <TabsContext.Provider value={tabsState}>
            <TabsDispatchContext.Provider value={mockedDispatch ?? dispatchTabs}>
                {children}
            </TabsDispatchContext.Provider>
        </TabsContext.Provider>
    );
};

const useTabs = (): TabsState => {
    const context = useContext(TabsContext);
    if (context === null) {
        throw new Error('useTabs must be used within a TabsProvider');
    }
    return context;
};

const useTabsDispatch = (): ActionDispatch<[action: TabAction]> => {
    const context = useContext(TabsDispatchContext);
    if (context === null) {
        throw new Error('useTabsDispatch must be used within a TabsProvider');
    }
    return context;
};

export { TabsProvider, useTabs, useTabsDispatch, tabReducer };
